import {
  DocumentGlobe20Filled,
  DocumentGlobe20Regular,
  Globe20Regular,
  Library20Regular,
  Screenshot20Filled,
  Screenshot20Regular,
} from '@fluentui/react-icons';
import { GlobalLibraryRegular } from '@shared/icons';
import { useParams } from 'react-router-dom';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DropEvent, useDropzone } from 'react-dropzone';
import { TableRowId } from '@fluentui/react-components';

import ActionItemsTable from '../components/ActionItemsTable';
import FoldersNavDrawer from '../components/nav/FoldersNavDrawer';
import Toolbar from '../components/Toolbar';
import SelectFolderDialog from '../components/dialogs/SelectFolderDialog';
import ExportDialog from '../components/dialogs/ExportDialog';
import VersionPreferenceDialog from '../components/dialogs/VersionPreferenceDialog';

import { useFolder, useMarkFolderViewed } from '../api/folders.api';
import useActions from '../hooks/useActions.hook';

import {
  TSelectFolderDialogState,
  IFolder,
  EFolderType,
  isTFolder,
  ECountriesFolderType,
  ESnapshotsFolderType,
} from '../types/folders.types';

import { SelectVersionDialog } from '@modules/documents';
import {
  TItemType,
  useMove,
  TSelectVersionDialogState,
  TExportDialogState,
  TVersionPreferenceDialogState,
  TSelectItemDialogState,
  SelectItemDialog,
  isTemplateSelectable,
  getTemplateDisabledReason,
  TEMPLATE_SEARCH_OPTIONS,
} from '@modules/items';
import { AccessDialog, AccessType, TAccessDialogState } from '@modules/access';
import { SignInContext } from '@modules/users';
import { EAiAssistantOriginItemType, useRegisterAiAssistantOrigin } from '@modules/ai';

import {
  StatusLabel,
  Path,
  SingleFieldDialog,
  TSingleFieldDialogState,
  ConfirmDialog,
  TConfirmDialogState,
  OptionsDialog,
} from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { useDocumentTitle, useFolderPathAsBreadcrumbs } from '@shared/hooks';
import useFileUpload from '../hooks/useFileUpload.hook';
import NameAndProductsDialog, { TNameAndProductsDialogState } from '../components/dialogs/NameAndProductsDialog';
import { TERMINOLOGY } from '@shared/i18n';
import { getFilesFromDropEvent, TSkippedDropzoneEntry } from '../helpers/dropzoneFiles.helper';
import OpenDashboardButton from '@modules/dashboard/components/OpenDashboardButton';
import { buildDashboardHref } from '@modules/dashboard/helpers/dashboardScope';
import RegistrationDetailsSection from '@modules/registrations/components/RegistrationDetailsSection';

type TProps = {
  folder: IFolder;
};

const GROUNDED_QA_ENABLED_FOLDER_TYPES: ReadonlySet<EFolderType | undefined> = new Set([
  undefined,
  EFolderType.NORMAL,
  EFolderType.PRODUCT,
  EFolderType.JURISDICTION,
  EFolderType.QM,
]);

const FolderContent = ({ folder }: TProps) => {
  const origin = GROUNDED_QA_ENABLED_FOLDER_TYPES.has(folder.folderType)
    ? { item: folder._id, itemType: EAiAssistantOriginItemType.FOLDER }
    : undefined;
  useRegisterAiAssistantOrigin(origin);

  const isBlockFolderType = folder.folderType === EFolderType.BLOCK;
  const isCountryFolderType = folder.folderType === EFolderType.COUNTRY;
  const isSnapshotFolderType = folder.folderType === EFolderType.SNAPSHOT;
  const qmDashboardHref = folder.folderType === EFolderType.QM ? buildDashboardHref([folder._id]) : undefined;

  let countriesFolderType: ECountriesFolderType | undefined;
  if (isCountryFolderType) {
    if (folder.resolvedPath.length === 0) countriesFolderType = ECountriesFolderType.ROOT;
    else if (folder.resolvedPath.length === 1) countriesFolderType = ECountriesFolderType.COUNTRY;
    // more than 2 means normal FS view (yet still a registrations folder, but not root or country)
  }

  let snapshotsFolderType: ESnapshotsFolderType | undefined;
  if (isSnapshotFolderType) {
    if (folder.resolvedPath.length === 1) snapshotsFolderType = ESnapshotsFolderType.ROOT;
    else if (folder.resolvedPath.length === 2) snapshotsFolderType = ESnapshotsFolderType.SNAPSHOT_COLLECTION;
    // more than 2 means normal FS view (yet still a snapshots folder)
  }

  const fullPath = [...folder.resolvedPath, { _id: folder._id, name: folder.name, accessRight: folder.accessRight }];
  if (isCountryFolderType && fullPath.length > 0) fullPath[0] = { ...fullPath[0], icon: Globe20Regular };

  if (fullPath.length > 1) {
    if (isBlockFolderType)
      fullPath[1] = { ...fullPath[1], icon: folder.isQm ? GlobalLibraryRegular : Library20Regular };
    if (isCountryFolderType) {
      if (fullPath.length > 2)
        fullPath[2] = { ...fullPath[2], icon: folder.isFrozen ? DocumentGlobe20Filled : DocumentGlobe20Regular };
    }
    if (isSnapshotFolderType && fullPath.length > 3)
      fullPath[3] = { ...fullPath[3], icon: folder.isFrozen ? Screenshot20Filled : Screenshot20Regular };
  }

  const [selectedItemIds, setSelectedItemIds] = useState(new Set<TableRowId>());

  const [nameAndProductsDialogState, setNameAndProductsDialogState] = useState<TNameAndProductsDialogState | undefined>(
    undefined
  );
  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);
  const [moveDialogState, setMoveDialogState] = useState<TSelectFolderDialogState | undefined>(undefined);
  const [replicaDialogState, setReplicaDialogState] = useState<TSelectFolderDialogState | undefined>(undefined);
  const [selectVersionDialogState, setSelectVersionDialogState] = useState<TSelectVersionDialogState | undefined>(
    undefined
  );
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const [exportDialogState, setExportDialogState] = useState<TExportDialogState | undefined>(undefined);
  const [versionPreferenceDialogState, setVersionPreferenceDialogState] = useState<
    TVersionPreferenceDialogState | undefined
  >(undefined);
  const [pullDialogState, setPullDialogState] = useState<TSelectItemDialogState | undefined>(undefined);
  const [cloneStructureDialogState, setCloneStructureDialogState] = useState<TSelectItemDialogState | undefined>(
    undefined
  );
  const [templateDialogState, setTemplateDialogState] = useState<TSelectItemDialogState | undefined>();
  const actions = useActions(
    setSimpleDialogState,
    setMoveDialogState,
    setReplicaDialogState,
    setSelectVersionDialogState,
    setConfirmDialogState,
    setAccessDialogState,
    setExportDialogState,
    setVersionPreferenceDialogState,
    setPullDialogState,
    setCloneStructureDialogState,
    folder,
    isBlockFolderType,
    countriesFolderType,
    snapshotsFolderType
  );

  const { handleFilesUpload, optionsDialogState, setOptionsDialogState } = useFileUpload({
    folder,
    isBlockFolderType,
    countriesFolderType: !!countriesFolderType,
    snapshotsFolderType: !!snapshotsFolderType,
  });
  const { show } = useContext(ToastContext);
  const { organization } = useContext(SignInContext);

  const handleSkippedDropzoneEntries = useCallback(
    (skippedEntries: TSkippedDropzoneEntry[]) => {
      if (skippedEntries.length === 0) return;
      show(
        'warning',
        `${skippedEntries.length} folder entr${
          skippedEntries.length === 1 ? 'y was' : 'ies were'
        } skipped because the browser could not read them. This often happens with very long Windows paths.`,
        { timeout: 10000 }
      );
    },
    [show]
  );

  const getFilesFromEvent = useCallback(
    (event: DropEvent) => getFilesFromDropEvent(event, { onSkippedEntries: handleSkippedDropzoneEntries }),
    [handleSkippedDropzoneEntries]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFilesUpload,
    getFilesFromEvent,
    onError: (error) => {
      console.error(error);
      show('error', 'Reading dropped files failed. Please check the selection and try again.', { timeout: 10000 });
    },
    multiple: true,
    // disabled: folder.accessRight < AccessType.EDIT,
    noClick: true,
    noKeyboard: true,
  });

  useDocumentTitle(folder.name);

  const move = useMove();
  // TODO: support multi-drag-and-drop (requires changing TOnMove to accept arrays + multi-item drag visual feedback)
  const onMove = useCallback(
    (sourceId: string, itemType: TItemType, targetId: string) => {
      move.mutate({
        parent: folder,
        items: [{ _id: sourceId, itemType }],
        targetId,
      });
    },
    [folder._id]
  );
  // conditionally set the selected navigation item
  let selectedNavItem = 'overview';
  if (isBlockFolderType) {
    selectedNavItem = folder.isQm ? 'global-library' : 'product-library';
  } else if (isCountryFolderType) {
    selectedNavItem = 'registrations';
  } else if (isSnapshotFolderType) {
    selectedNavItem = 'snapshots';
  } else if (!folder.isQm) {
    selectedNavItem = fullPath[1]?._id || 'overview';
  }

  const children = folder.resolvedPath.length
    ? folder.children
    : folder.children.filter((item) => !item.isBlock && item._id !== folder.snapshotsFolder?._id);

  const foldersModifiedToday = organization.mock?.foldersModifiedToday ?? [];
  const isInFolderModifiedToday = [folder._id, ...folder.resolvedPath.map(({ _id }) => _id)].some((id) =>
    foldersModifiedToday.includes(id)
  );
  const items = isInFolderModifiedToday
    ? children.map((item) => ({ ...item, stats: { ...item.stats, modifiedDate: new Date().toISOString() } }))
    : children;

  // open pull dialog in the first selected product of this country registration if available:
  let pullInitialFolderId: string | undefined =
    folder.resolvedPath.length > 0 ? folder.resolvedPath[0]._id : folder._id; // fallback
  if (isCountryFolderType) pullInitialFolderId = folder.pullPreference;

  const templateInitialFolderId = folder.resolvedPath[0]?._id || folder._id; // this might be configurable per org later

  return (
    <div className="flex flex-1" {...getRootProps()}>
      <input
        {...getInputProps()}
        style={{
          position: 'absolute',
          top: '-9999px',
        }}
      />
      <FoldersNavDrawer
        folder={folder}
        fullPath={fullPath}
        selectedValue={selectedNavItem} // first element is undefined!
      />
      <div className="grow h-full w-full flex flex-col">
        <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
        <NameAndProductsDialog
          title={`New ${TERMINOLOGY.registrationSingle.lower}`}
          nameLabel="Registration name"
          productsField={{ label: 'Products', multiselect: true, required: true }}
          setState={setNameAndProductsDialogState}
          state={nameAndProductsDialogState}
        />
        <OptionsDialog state={optionsDialogState} setState={setOptionsDialogState} />
        <SelectFolderDialog
          title={`Move item${moveDialogState?.affectedItems?.length !== 1 ? 's' : ''}`}
          buttonLabel="Move here"
          sourceId={folder._id}
          navigatableFolderTypes={isBlockFolderType ? ['BLOCK'] : ['NORMAL', 'COUNTRY', 'SNAPSHOT']}
          requireFs
          disabledFolderTypes={isBlockFolderType ? [EFolderType.QM] : []}
          state={moveDialogState}
          setState={setMoveDialogState}
        />
        <SelectFolderDialog
          title={`${TERMINOLOGY.synchronize.upper} item${replicaDialogState?.affectedItems?.length !== 1 ? 's' : ''}`}
          buttonLabel={`${TERMINOLOGY.synchronize.upper} item${replicaDialogState?.affectedItems?.length !== 1 ? 's' : ''} here`}
          sourceId={folder.resolvedPath[0]?._id || folder._id}
          navigatableFolderTypes={['NORMAL', 'COUNTRY']}
          requireFs
          state={replicaDialogState}
          setState={setReplicaDialogState}
        />
        <SelectVersionDialog state={selectVersionDialogState} setState={setSelectVersionDialogState} />
        <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
        <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
        <ExportDialog state={exportDialogState} setState={setExportDialogState} />
        <VersionPreferenceDialog state={versionPreferenceDialogState} setState={setVersionPreferenceDialogState} />
        <SelectItemDialog
          initialFolderId={pullInitialFolderId}
          title="Select items to pull"
          actionText="Pull"
          navigatableFolderTypes={['NORMAL']}
          selectableTypes={['FOLDER', 'DOCUMENT', 'FILE', 'REPLICA_DOCUMENT', 'REPLICA_FILE']}
          isSelectable={(parent, item) => {
            if (!isTFolder(parent))
              // no qm, products
              return false;
            if (item.type === 'FOLDER' && item.folderType === EFolderType.COUNTRY && item.path.length <= 1)
              return false;
            return true;
          }}
          multiSelect
          state={pullDialogState}
          setState={setPullDialogState}
        />
        <SelectItemDialog
          title={`Select a ${TERMINOLOGY.folder.lower} to clone`}
          actionText="Clone"
          navigatableFolderTypes={['BLOCK']}
          selectableTypes={['FOLDER']}
          isSelectable={(_parent, item) =>
            item.isBlock &&
            item.accessRight >= AccessType.READ &&
            !fullPath.some((pathItem) => pathItem._id === item._id) &&
            !item.path.includes(folder._id)
          }
          state={cloneStructureDialogState}
          setState={setCloneStructureDialogState}
        />
        <SelectItemDialog
          initialFolderId={templateInitialFolderId}
          title={`Create from ${TERMINOLOGY.template.lower}`}
          actionText="Create"
          navigatableFolderTypes={['NORMAL', 'COUNTRY']}
          selectableTypes={['DOCUMENT']}
          isSelectable={isTemplateSelectable}
          getDisabledReason={getTemplateDisabledReason}
          enableSearch
          searchOptions={TEMPLATE_SEARCH_OPTIONS}
          state={templateDialogState}
          setState={setTemplateDialogState}
        />

        <div className="relative h-16 w-full">
          <div className="absolute top-0 left-0 z-10 w-full pl-1 pr-7 pt-1">
            <Toolbar
              folder={folder}
              selectedItemIds={selectedItemIds}
              setSelectedItemIds={setSelectedItemIds}
              actions={actions}
              setSimpleDialogState={setSimpleDialogState}
              setNameAndProductsDialogState={setNameAndProductsDialogState}
              handleFilesUpload={handleFilesUpload}
              isBlockFolderType={isBlockFolderType}
              countriesFolderType={countriesFolderType}
              snapshotsFolderType={snapshotsFolderType}
              setTemplateDialogState={setTemplateDialogState}
            />
          </div>
        </div>

        <div className="grow h-full w-full relative">
          <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
            <div className="flex flex-col space-y-3 pt-4 pb-16 pl-5 pr-8">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 grow">
                  <Path items={useFolderPathAsBreadcrumbs(fullPath, { folder, onMove })} hasCurrent size="large" />
                </div>
                {qmDashboardHref && (
                  <div className="shrink-0">
                    <OpenDashboardButton href={qmDashboardHref} />
                  </div>
                )}
              </div>
              <RegistrationDetailsSection folder={folder} />
              <ActionItemsTable
                folder={folder}
                items={items}
                selectedItemIds={selectedItemIds as Set<string>}
                setSelectedItemIds={setSelectedItemIds}
                actions={actions}
                id={folder._id}
                resolvedNames={folder.resolvedNames}
                fullPath={fullPath}
                isBlockFolderType={isBlockFolderType}
                countriesFolderType={countriesFolderType}
                snapshotsFolderType={snapshotsFolderType}
                onMove={onMove}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Folder = () => {
  const { id } = useParams(); // Access the dynamic part of the route
  const { error, data } = useFolder(id as string); // id is always filled, otherwise NoPage.tsx is rendered
  const markFolderViewed = useMarkFolderViewed();

  useEffect(() => {
    markFolderViewed.mutate(id as string);
  }, [id]);

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading folder..."
      error={error}
      errorMessage="Loading folder failed!"
      render={(data) => isTFolder(data) && <FolderContent folder={data} />}
    />
  );
};

export default Folder;
